import { useState, useEffect } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  CreditCard, Plus, Edit, Trash2, Eye, FileText, Loader2,
  AlertCircle, DollarSign, CheckCircle2, Lock, X, Search
} from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

const BANDEIRAS = ["Visa", "Master", "Elo", "Amex", "Hipercard", "Diners", "Outra"];

function formatBRL(v) {
  return `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function CartoesCreditoPage() {
  const [tab, setTab] = useState("cartoes"); // cartoes | despesas | faturas | dashboard
  const [cartoes, setCartoes] = useState([]);
  const [despesas, setDespesas] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [fluxo, setFluxo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCardForm, setShowCardForm] = useState(false);
  const [editCard, setEditCard] = useState(null);
  const [cardForm, setCardForm] = useState({
    apelido: "", banco: "", bandeira: "Visa", ultimos_4_digitos: "", titular: "",
    dia_vencimento: 10, melhor_dia_compra: 1, limite: 0, status: "ativo", observacoes: ""
  });
  const [showExpForm, setShowExpForm] = useState(false);
  const [expForm, setExpForm] = useState({
    card_id: "", data_despesa: new Date().toISOString().split("T")[0],
    valor_total: 0, parcelas: 1, estabelecimento: "",
    descricao: "", categoria_id: "", categoria_nome: "", centro_custo_id: "", observacoes: ""
  });
  const [categorias, setCategorias] = useState([]);
  const [centrosCusto, setCentrosCusto] = useState([]);
  const [viewExp, setViewExp] = useState(null);
  const [showFechar, setShowFechar] = useState(false);
  const [fecharData, setFecharData] = useState({ card_id: "", competencia: "", parcelas: [], total: 0, selected: new Set() });
  const [deleteCard, setDeleteCard] = useState(null);
  const [deleteExp, setDeleteExp] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");

  const token = sessionStorage.getItem("luvcorp_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, e, d, f, cat, cc] = await Promise.all([
        axios.get(`${API}/credit-cards`, { headers }),
        axios.get(`${API}/credit-card-expenses`, { headers }),
        axios.get(`${API}/credit-cards/dashboard/resumo`, { headers }),
        axios.get(`${API}/credit-cards/dashboard/fluxo?meses=6`, { headers }),
        axios.get(`${API}/categorias?tipo=despesa`, { headers }),
        axios.get(`${API}/centros-custo?ativo=true`, { headers }),
      ]);
      setCartoes(c.data || []);
      setDespesas(e.data || []);
      setDashboard(d.data);
      setFluxo(f.data);
      setCategorias(cat.data || []);
      setCentrosCusto(cc.data || []);
    } catch { toast.error("Erro ao carregar dados"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSaveCard = async () => {
    if (!cardForm.apelido) { toast.error("Apelido obrigatorio"); return; }
    try {
      if (editCard) {
        await axios.put(`${API}/credit-cards/${editCard.id}`, cardForm, { headers });
        toast.success("Cartão atualizado");
      } else {
        await axios.post(`${API}/credit-cards`, cardForm, { headers });
        toast.success("Cartão criado");
      }
      setShowCardForm(false); setEditCard(null);
      setCardForm({ apelido: "", banco: "", bandeira: "Visa", ultimos_4_digitos: "", titular: "", dia_vencimento: 10, melhor_dia_compra: 1, limite: 0, status: "ativo", observacoes: "" });
      fetchAll();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro")); }
  };

  const handleSaveExp = async () => {
    if (!expForm.card_id || !expForm.estabelecimento || expForm.valor_total <= 0) { toast.error("Preencha cartao, estabelecimento e valor"); return; }
    try {
      await axios.post(`${API}/credit-card-expenses`, expForm, { headers });
      toast.success("Despesa registrada");
      setShowExpForm(false);
      setExpForm({ card_id: "", data_despesa: new Date().toISOString().split("T")[0], valor_total: 0, parcelas: 1, estabelecimento: "", descricao: "", categoria_id: "", categoria_nome: "", centro_custo_id: "", observacoes: "" });
      fetchAll();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro")); }
  };

  const handleDeleteCard = async () => {
    if (!deleteCard) return;
    try {
      await axios.delete(`${API}/credit-cards/${deleteCard.id}`, { headers });
      toast.success("Cartão desativado");
      setDeleteCard(null);
      fetchAll();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro")); }
  };

  const handleDeleteExp = async () => {
    if (!deleteExp || !deletePassword) { toast.error("Senha obrigatória"); return; }
    try {
      await axios.delete(`${API}/credit-card-expenses/${deleteExp.id}`, { data: { password: deletePassword }, headers, skipAuthInterceptor: true });
      toast.success("Despesa excluída");
      setDeleteExp(null); setDeletePassword("");
      fetchAll();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro")); }
  };

  const openFecharFatura = async (cardId, competencia) => {
    try {
      const r = await axios.get(`${API}/credit-cards/${cardId}/statements/preview?competencia=${competencia}`, { headers });
      if (r.data.fechada) { toast.error("Fatura ja fechada"); return; }
      setFecharData({
        card_id: cardId, competencia, parcelas: r.data.parcelas,
        total: r.data.total, selected: new Set(r.data.parcelas.map(p => p.id))
      });
      setShowFechar(true);
    } catch { toast.error("Erro"); }
  };

  const handleFecharFatura = async () => {
    if (fecharData.selected.size === 0) { toast.error("Selecione parcelas"); return; }
    try {
      await axios.post(`${API}/credit-cards/${fecharData.card_id}/statements/fechar`, {
        competencia: fecharData.competencia, parcela_ids: [...fecharData.selected]
      }, { headers });
      toast.success("Fatura fechada e lançamento gerado em Contas a Pagar");
      setShowFechar(false);
      fetchAll();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro")); }
  };

  if (loading) return <MainLayout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#0A1628]" /></div></MainLayout>;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="cartoes-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-heading font-bold text-[#0A1628] flex items-center gap-2">
              <CreditCard className="h-6 w-6" />Cartões de Crédito
            </h1>
            <p className="text-sm text-slate-500 mt-1">Gerenciamento corporativo de cartões e faturas.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { setEditCard(null); setShowCardForm(true); }} className="bg-[#0A1628]" data-testid="btn-novo-cartao"><Plus className="h-4 w-4 mr-1" />Novo Cartão</Button>
            <Button onClick={() => setShowExpForm(true)} variant="outline" data-testid="btn-nova-despesa"><Plus className="h-4 w-4 mr-1" />Nova Despesa</Button>
          </div>
        </div>

        {/* KPIs Dashboard */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Total em aberto</p><p className="text-2xl font-bold text-red-600">{formatBRL(dashboard.total_em_aberto)}</p></CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Cartões ativos</p><p className="text-2xl font-bold text-[#0A1628]">{dashboard.qtd_cartoes_ativos}</p></CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Parcelamentos ativos</p><p className="text-2xl font-bold text-amber-600">{dashboard.parcelamentos_ativos}</p></CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Faturas fechadas</p><p className="text-2xl font-bold text-emerald-600">{dashboard.faturas_fechadas_count}</p></CardContent></Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          {["cartoes", "despesas", "faturas", "fluxo"].map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 capitalize ${tab === t ? "border-[#0A1628] text-[#0A1628]" : "border-transparent text-slate-500"}`}>
              {t === "cartoes" ? "Cartões" : t === "despesas" ? "Despesas" : t === "faturas" ? "Faturas" : "Fluxo de Caixa"}
            </button>
          ))}
        </div>

        {tab === "cartoes" && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Apelido</TableHead><TableHead>Banco/Bandeira</TableHead><TableHead>Final</TableHead>
                  <TableHead>Venc.</TableHead><TableHead>Limite</TableHead><TableHead>Em aberto</TableHead>
                  <TableHead>Utilização</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Ações</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {cartoes.map(c => (
                    <TableRow key={c.id} data-testid={`card-row-${c.id}`}>
                      <TableCell className="font-medium">{c.apelido}</TableCell>
                      <TableCell className="text-sm">{c.banco} {c.bandeira && `· ${c.bandeira}`}</TableCell>
                      <TableCell className="font-mono text-xs">****{c.ultimos_4_digitos}</TableCell>
                      <TableCell>dia {c.dia_vencimento}</TableCell>
                      <TableCell className="font-mono text-xs">{formatBRL(c.limite)}</TableCell>
                      <TableCell className="font-mono text-xs text-red-600">{formatBRL(c.total_em_aberto)}</TableCell>
                      <TableCell><Badge className={c.utilizacao_pct > 80 ? "bg-red-100 text-red-700" : c.utilizacao_pct > 50 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}>{c.utilizacao_pct}%</Badge></TableCell>
                      <TableCell><Badge variant={c.status === "ativo" ? "default" : "outline"}>{c.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => { setEditCard(c); setCardForm(c); setShowCardForm(true); }} data-testid={`btn-edit-card-${c.id}`}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeleteCard(c)} data-testid={`btn-del-card-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {cartoes.length === 0 && <p className="p-8 text-center text-sm text-slate-400">Nenhum cartão cadastrado</p>}
            </CardContent>
          </Card>
        )}

        {tab === "despesas" && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Data</TableHead><TableHead>Cartão</TableHead><TableHead>Estabelecimento</TableHead>
                  <TableHead>Categoria</TableHead><TableHead>Total</TableHead><TableHead>Parcelas</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {despesas.map(e => (
                    <TableRow key={e.id} data-testid={`exp-row-${e.id}`}>
                      <TableCell>{e.data_despesa}</TableCell>
                      <TableCell>{e.card_apelido}</TableCell>
                      <TableCell>{e.estabelecimento}</TableCell>
                      <TableCell className="text-xs">{e.categoria_nome || "-"}</TableCell>
                      <TableCell className="font-mono">{formatBRL(e.valor_total)}</TableCell>
                      <TableCell><Badge variant="outline">{e.parcelas}x</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setViewExp(e)} data-testid={`btn-view-exp-${e.id}`}><Eye className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeleteExp(e)} data-testid={`btn-del-exp-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {despesas.length === 0 && <p className="p-8 text-center text-sm text-slate-400">Nenhuma despesa registrada</p>}
            </CardContent>
          </Card>
        )}

        {tab === "faturas" && dashboard && (
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle className="text-base">Faturas em Aberto</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {cartoes.map(c => {
                const competencias = dashboard.faturas_abertas_periodos || [];
                return competencias.map(comp => (
                  <div key={c.id + comp} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`fatura-${c.id}-${comp}`}>
                    <div>
                      <p className="font-medium text-[#0A1628]">{c.apelido}</p>
                      <p className="text-xs text-slate-500">Competência {comp}</p>
                    </div>
                    <Button size="sm" onClick={() => openFecharFatura(c.id, comp)} className="bg-[#0A1628]" data-testid={`btn-fechar-${c.id}-${comp}`}>
                      Fechar fatura
                    </Button>
                  </div>
                ));
              })}
              {(dashboard.faturas_abertas_periodos || []).length === 0 && <p className="text-center text-sm text-slate-400">Nenhuma fatura em aberto</p>}
            </CardContent>
          </Card>
        )}

        {tab === "fluxo" && fluxo && (
          <Card className="border-0 shadow-sm" data-testid="fluxo-cartoes">
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span>Projeção de Saídas · Próximos {fluxo.meses_projetados} meses</span>
                <span className="text-sm font-mono text-red-600" data-testid="fluxo-total-geral">Total: {formatBRL(fluxo.total_geral)}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {fluxo.competencias.map(c => {
                const maxTotal = Math.max(...fluxo.competencias.map(x => x.total)) || 1;
                const pct = (c.total / maxTotal) * 100;
                return (
                  <div key={c.competencia} className="p-3 rounded-lg border bg-white" data-testid={`fluxo-row-${c.competencia}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-semibold text-[#0A1628]">{c.competencia}</p>
                        <p className="text-xs text-slate-500">
                          {c.qtd} parcela{c.qtd === 1 ? "" : "s"}
                          {c.data_vencimento && ` · 1º venc ${new Date(c.data_vencimento + "T00:00:00").toLocaleDateString("pt-BR")}`}
                        </p>
                      </div>
                      <p className="font-mono text-lg font-bold text-red-600">{formatBRL(c.total)}</p>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-2">
                      <div className="h-full bg-gradient-to-r from-red-400 to-red-600" style={{ width: `${pct}%` }} />
                    </div>
                    {c.cartoes.length > 0 ? (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {c.cartoes.map(cc => (
                          <Badge key={cc.card_id} variant="outline" className="text-xs">
                            {cc.apelido}: {formatBRL(cc.total)}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">Nenhuma parcela em aberto</p>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog Cartao */}
      <Dialog open={showCardForm} onOpenChange={(o) => { if (!o) { setShowCardForm(false); setEditCard(null); } }}>
        <DialogContent className="max-w-lg" data-testid="dialog-card-form">
          <DialogHeader><DialogTitle>{editCard ? "Editar Cartão" : "Novo Cartão"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2"><Label>Apelido *</Label><Input value={cardForm.apelido} onChange={e => setCardForm({ ...cardForm, apelido: e.target.value })} data-testid="input-apelido" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Banco</Label><Input value={cardForm.banco} onChange={e => setCardForm({ ...cardForm, banco: e.target.value })} /></div>
              <div className="space-y-2"><Label>Bandeira</Label>
                <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={cardForm.bandeira} onChange={e => setCardForm({ ...cardForm, bandeira: e.target.value })}>
                  {BANDEIRAS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div className="space-y-2"><Label>Últimos 4 dígitos</Label><Input maxLength={4} value={cardForm.ultimos_4_digitos} onChange={e => setCardForm({ ...cardForm, ultimos_4_digitos: e.target.value.replace(/\D/g, "") })} /></div>
              <div className="space-y-2"><Label>Titular</Label><Input value={cardForm.titular} onChange={e => setCardForm({ ...cardForm, titular: e.target.value })} /></div>
              <div className="space-y-2"><Label>Dia vencimento</Label><Input type="number" min={1} max={31} value={cardForm.dia_vencimento} onChange={e => setCardForm({ ...cardForm, dia_vencimento: parseInt(e.target.value) || 1 })} /></div>
              <div className="space-y-2"><Label>Melhor dia compra</Label><Input type="number" min={1} max={31} value={cardForm.melhor_dia_compra} onChange={e => setCardForm({ ...cardForm, melhor_dia_compra: parseInt(e.target.value) || 1 })} /></div>
              <div className="space-y-2 col-span-2"><Label>Limite (R$)</Label><Input type="number" step="0.01" value={cardForm.limite} onChange={e => setCardForm({ ...cardForm, limite: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div className="space-y-2"><Label>Observações</Label><Textarea value={cardForm.observacoes} onChange={e => setCardForm({ ...cardForm, observacoes: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCardForm(false)}>Cancelar</Button>
            <Button onClick={handleSaveCard} className="bg-[#0A1628]" data-testid="btn-save-card">{editCard ? "Atualizar" : "Criar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Despesa */}
      <Dialog open={showExpForm} onOpenChange={setShowExpForm}>
        <DialogContent className="max-w-lg" data-testid="dialog-exp-form">
          <DialogHeader><DialogTitle>Nova Despesa</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2"><Label>Cartão *</Label>
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={expForm.card_id} onChange={e => setExpForm({ ...expForm, card_id: e.target.value })} data-testid="select-card">
                <option value="">Selecione...</option>
                {cartoes.filter(c => c.status === "ativo").map(c => <option key={c.id} value={c.id}>{c.apelido}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Data despesa *</Label><Input type="date" value={expForm.data_despesa} onChange={e => setExpForm({ ...expForm, data_despesa: e.target.value })} /></div>
              <div className="space-y-2"><Label>Valor total (R$) *</Label><Input type="number" step="0.01" value={expForm.valor_total} onChange={e => setExpForm({ ...expForm, valor_total: parseFloat(e.target.value) || 0 })} /></div>
              <div className="space-y-2"><Label>Parcelas</Label><Input type="number" min={1} max={36} value={expForm.parcelas} onChange={e => setExpForm({ ...expForm, parcelas: parseInt(e.target.value) || 1 })} /></div>
              <div className="space-y-2"><Label>Valor parcela</Label><Input disabled value={formatBRL(expForm.valor_total / (expForm.parcelas || 1))} /></div>
            </div>
            <div className="space-y-2"><Label>Estabelecimento *</Label><Input value={expForm.estabelecimento} onChange={e => setExpForm({ ...expForm, estabelecimento: e.target.value })} data-testid="input-estabelecimento" /></div>
            <div className="space-y-2"><Label>Descrição</Label><Input value={expForm.descricao} onChange={e => setExpForm({ ...expForm, descricao: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoria</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
                  value={expForm.categoria_id}
                  onChange={e => {
                    const cat = categorias.find(c => c.id === e.target.value);
                    setExpForm({ ...expForm, categoria_id: e.target.value, categoria_nome: cat?.nome || "" });
                  }}
                  data-testid="select-categoria-exp"
                >
                  <option value="">-- Selecione --</option>
                  {categorias.map(c => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Centro de Custo</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
                  value={expForm.centro_custo_id}
                  onChange={e => setExpForm({ ...expForm, centro_custo_id: e.target.value })}
                  data-testid="select-centro-custo-exp"
                >
                  <option value="">-- Selecione --</option>
                  {centrosCusto.map(cc => (
                    <option key={cc.id} value={cc.id}>{cc.codigo} - {cc.nome}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-2"><Label>Observações</Label><Textarea value={expForm.observacoes} onChange={e => setExpForm({ ...expForm, observacoes: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExpForm(false)}>Cancelar</Button>
            <Button onClick={handleSaveExp} className="bg-[#0A1628]" data-testid="btn-save-exp">Registrar despesa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visualizar Despesa */}
      <Dialog open={!!viewExp} onOpenChange={(o) => !o && setViewExp(null)}>
        <DialogContent className="max-w-2xl" data-testid="dialog-view-exp">
          <DialogHeader><DialogTitle>Detalhes da Despesa</DialogTitle></DialogHeader>
          {viewExp && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-500">Cartão</Label><p className="font-medium">{viewExp.card_apelido}</p></div>
                <div><Label className="text-xs text-slate-500">Data</Label><p>{viewExp.data_despesa}</p></div>
                <div className="col-span-2"><Label className="text-xs text-slate-500">Estabelecimento</Label><p className="font-medium">{viewExp.estabelecimento}</p></div>
                <div><Label className="text-xs text-slate-500">Valor total</Label><p className="font-mono">{formatBRL(viewExp.valor_total)}</p></div>
                <div><Label className="text-xs text-slate-500">Parcelas</Label><p>{viewExp.parcelas}x</p></div>
                <div className="col-span-2"><Label className="text-xs text-slate-500">Categoria</Label><p>{viewExp.categoria_nome || "-"}</p></div>
                <div className="col-span-2"><Label className="text-xs text-slate-500">Descrição</Label><p>{viewExp.descricao || "-"}</p></div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Cronograma de Parcelas</Label>
                <Table className="mt-2"><TableHeader><TableRow><TableHead>#</TableHead><TableHead>Competência</TableHead><TableHead>Venc</TableHead><TableHead>Valor</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>{(viewExp.parcelas_detalhes || []).map(p => (
                    <TableRow key={p.id}><TableCell>{p.numero}/{p.total}</TableCell><TableCell>{p.competencia}</TableCell><TableCell>{p.data_vencimento}</TableCell><TableCell className="font-mono">{formatBRL(p.valor)}</TableCell><TableCell><Badge variant={p.status === "aberta" ? "outline" : "default"}>{p.status}</Badge></TableCell></TableRow>
                  ))}</TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fechar Fatura */}
      <Dialog open={showFechar} onOpenChange={setShowFechar}>
        <DialogContent className="max-w-3xl" data-testid="dialog-fechar-fatura">
          <DialogHeader><DialogTitle>Fechar Fatura · Competência {fecharData.competencia}</DialogTitle>
            <DialogDescription>Após fechar, será gerado lançamento em Contas a Pagar e as parcelas selecionadas serão marcadas como fechadas.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-auto">
            <Table><TableHeader><TableRow><TableHead></TableHead><TableHead>Despesa</TableHead><TableHead>Parcela</TableHead><TableHead>Venc</TableHead><TableHead>Valor</TableHead></TableRow></TableHeader>
              <TableBody>{fecharData.parcelas.map(p => (
                <TableRow key={p.id}>
                  <TableCell><input type="checkbox" checked={fecharData.selected.has(p.id)} onChange={e => {
                    const ns = new Set(fecharData.selected);
                    if (e.target.checked) ns.add(p.id); else ns.delete(p.id);
                    setFecharData({ ...fecharData, selected: ns });
                  }} /></TableCell>
                  <TableCell className="text-xs">{p.expense_id?.slice(0, 8)}</TableCell>
                  <TableCell>{p.numero}/{p.total}</TableCell>
                  <TableCell>{p.data_vencimento}</TableCell>
                  <TableCell className="font-mono">{formatBRL(p.valor)}</TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
            <div className="text-right text-lg font-semibold pt-3 border-t">Total selecionado: {formatBRL([...fecharData.parcelas].filter(p => fecharData.selected.has(p.id)).reduce((s, p) => s + p.valor, 0))}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFechar(false)}>Cancelar</Button>
            <Button onClick={handleFecharFatura} className="bg-emerald-600" data-testid="btn-confirm-fechar">Fechar fatura e gerar lançamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Cartao */}
      <Dialog open={!!deleteCard} onOpenChange={(o) => !o && setDeleteCard(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-red-600">Desativar cartão?</DialogTitle><DialogDescription>Histórico será preservado. Cartão com parcelas em aberto não pode ser desativado.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteCard(null)}>Cancelar</Button><Button className="bg-red-600" onClick={handleDeleteCard}>Desativar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Despesa */}
      <Dialog open={!!deleteExp} onOpenChange={(o) => { if (!o) { setDeleteExp(null); setDeletePassword(""); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-del-exp">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2"><AlertCircle className="h-5 w-5" />Excluir Despesa?</DialogTitle>
            <DialogDescription>Ação irreversível. Digite sua senha para confirmar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2"><Label className="flex items-center gap-1"><Lock className="h-3 w-3" />Senha</Label><Input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} data-testid="input-del-exp-pwd" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteExp(null)}>Cancelar</Button><Button className="bg-red-600" onClick={handleDeleteExp} disabled={!deletePassword} data-testid="btn-confirm-del-exp">Excluir</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
